import React, { useCallback, useEffect, useState } from 'react';
import axios from '../axiosConfig';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Typography,
  TablePagination,
  LinearProgress,
  Tooltip,
} from '@mui/material';
// import { Download } from 'lucide-react';

const apiUrl = process.env.REACT_APP_API_URL;

interface ReportItem {
  id: number;
  fileName: string;
  generatedAt: string; 
  generatedBy: string;
  downloadUrl: string; 
}

interface ReportHistoryTableProps {
  processType: number; // 0 = PRE, 1 = POST
}

export const ReportHistoryTable: React.FC<ReportHistoryTableProps> = ({ processType }) => {
  const [reports, setReports] = useState<ReportItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [isLoading, setIsLoading] = useState(false);

  const fetchReports = useCallback(async (pageNumber: number, pageSize: number) => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${apiUrl}/Excel/history`, {
        params: {
          pageNumber: pageNumber + 1, // tu API es 1-based
          pageSize,
          processType,
        },
      });

      // Normalizamos items desde el backend.
      // Esperado: items con { id, fileName, generatedAt, generatedBy, downloadUrl }
      // Compat: si temporalmente sigue viniendo blobUrl, lo usamos como downloadUrl.
      const items: ReportItem[] = (response.data.items || []).map((x: any) => ({
        id: x.id,
        fileName: x.fileName,
        generatedAt: x.generatedAt,
        generatedBy: x.generatedBy,
        downloadUrl: x.downloadUrl ?? x.blobUrl, // compat temporal
      }));

      setReports(items);
      setTotalCount(response.data.totalCount ?? 0);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setIsLoading(false);
    }
  }, [processType]);

  useEffect(() => {
    fetchReports(page, rowsPerPage);
  }, [page, rowsPerPage, fetchReports]);

  const handleChangePage = (_: unknown, newPage: number) => setPage(newPage);

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };



  return (
    <div className="w-full">
      <Typography variant="h6" gutterBottom>
        History of previous generated reports
      </Typography>
      <TableContainer component={Paper}>
        {isLoading && <LinearProgress />}
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ maxWidth: 420, width: '40%' }}>File Name</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap', width: '20%' }}>Date &amp; Time</TableCell>
              <TableCell sx={{ maxWidth: 380, width: '30%' }}>Generated By</TableCell>
              <TableCell sx={{ whiteSpace: 'nowrap', width: '10%' }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reports.map((report) => (
              <TableRow key={report.id}>
                <TableCell sx={{ maxWidth: 420 }}>
                  <Tooltip title={report.fileName} arrow>
                    <span style={{ display: 'inline-block', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.95rem' }}>
                      {report.fileName}
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{new Date(report.generatedAt).toLocaleString()}</TableCell>
                <TableCell sx={{ maxWidth: 380 }}>
                  <Tooltip title={report.generatedBy} arrow>
                    <span style={{ display: 'inline-block', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.95rem' }}>
                      {report.generatedBy}
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell>
                <Button
                  component="a"
                  href={report.downloadUrl}
                >
                  Download
                </Button>
                                </TableCell>
              </TableRow>
            ))}
            {!isLoading && reports.length === 0 && (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  No reports found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 20]}
        />
      </TableContainer>
    </div>
  );
};
